// AdminDashboard: Main dashboard for super admin, admin, and staff
import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import AdminLayout from '../../layouts/AdminLayout';
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import StaffLayout from '../../layouts/StaffLayout';
import { Box, Typography } from '@mui/material';
import { Pie, Bar } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js';
Chart.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

function AdminDashboard() {
  // Get user role from localStorage and normalize (prefer 'admin' when multiple roles present)
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem('user')) || {};
  } catch (e) {}
  const normalizeRoles = (r) => {
    if (!r) return [];
    if (Array.isArray(r)) return r.map(x => String(x).toLowerCase());
    if (typeof r === 'string') return r.split(/[,;|]/).map(x => String(x).trim().toLowerCase()).filter(Boolean);
    return [String(r).toLowerCase()];
  };
  const roles = normalizeRoles(user.role);
  let role = 'guest';
  if (roles.includes('admin')) role = 'admin';
  else if (roles.includes('superadmin')) role = 'superadmin';
  else if (roles.includes('staff')) role = 'staff';

  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [apiError, setApiError] = useState(null);

  // Status colors for charts
  const statusColors = {
    sent: '#16a34a',
    failed: '#f43f5e',
    email: '#6366f1',
    push: '#0ea5e9',
    sms: '#eab308',
    staff_review: '#6366f1',
    approved: '#16a34a',
    pending: '#eab308',
    Available: '#0ea5a4',
    Booked: '#f59e42',
    Invited: '#0ea5e9'
  };

  useEffect(() => {
    // Check for token and user role before making API call
    const token = localStorage.getItem('token');
    if (!token || !role || !['superadmin', 'admin', 'staff'].includes(role)) {
      setApiError({
        message: 'You are not logged in or do not have permission to view the dashboard. Please log in with the correct account.',
        response: { data: { message: 'Not authenticated or not authorized.' } },
        responseStatus: 401
      });
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get('/api/dashboard')
      .then(res => {
        setStats(res.data);
        setApiError(null);
        setLoading(false);
      })
      .catch(err => {
        setApiError(err);
        setSnackbar({
          open: true,
          message: err?.response?.data?.message || 'Failed to load dashboard data',
          severity: 'error'
        });
        setLoading(false);
      });
  }, [role]);

  // Prepare chart data
  const bookingsData = stats.bookings ? {
    labels: Object.keys(stats.bookings),
    datasets: [{
      data: Object.values(stats.bookings),
      backgroundColor: Object.keys(stats.bookings).map(s => statusColors[s] || '#888'),
    }]
  } : null;
  const boothsData = stats.booths ? {
    labels: Object.keys(stats.booths),
    datasets: [{
      data: Object.values(stats.booths),
      backgroundColor: Object.keys(stats.booths).map(s => statusColors[s] || '#888'),
    }]
  } : null;
  const ticketsData = stats.tickets ? {
    labels: Object.keys(stats.tickets),
    datasets: [{
      data: Object.values(stats.tickets),
      backgroundColor: Object.keys(stats.tickets).map(s => statusColors[s] || '#888'),
    }]
  } : null;
  const paymentsData = stats.payments ? {
    labels: Object.keys(stats.payments),
    datasets: [{
      data: Object.values(stats.payments),
      backgroundColor: Object.keys(stats.payments).map(s => statusColors[s] || '#888'),
    }]
  } : null;

  // Choose layout based on role
  let Layout = AdminLayout;
  if (role === 'superadmin') Layout = SuperAdminLayout;
  if (role === 'staff') Layout = StaffLayout;

  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {role === 'superadmin' ? 'Super Admin Dashboard' : role === 'admin' ? 'Admin Dashboard' : 'Staff Dashboard'}
        </Typography>
        <NotificationSnackbar
          open={snackbar.open}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          message={snackbar.message}
          severity={snackbar.severity}
        />
        {/* Debug: Show raw stats object, error, and user role */}
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, fontSize: 13, color: '#444' }}>
          <strong>Debug Stats:</strong>
          <pre style={{ margin: 0 }}>{JSON.stringify(stats, null, 2)}</pre>
          <strong>User Role:</strong> {role}
          {apiError && <>
            <strong>API Error:</strong>
            <pre style={{ margin: 0, color: 'red' }}>{JSON.stringify({
              message: apiError?.message,
              response: apiError?.response?.data,
              status: apiError?.response?.status
            }, null, 2)}</pre>
          </>}
        </Box>
        {loading ? (
          <Typography>Loading dashboard stats...</Typography>
        ) : apiError ? (
          <Box sx={{ my: 4 }}>
            <Typography color="error" variant="h6" gutterBottom>
              {apiError?.response?.data?.message || apiError?.message || 'You are not authorized to view this dashboard.'}
            </Typography>
            <Typography color="text.secondary">
              Please log in with an account that has admin, superadmin, or staff permissions.
            </Typography>
          </Box>
        ) : (stats && Object.keys(stats).length > 0 && typeof stats === 'object') ? <>
            {/* KPI Cards */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 3,
              mb: 4
            }}>
              <StatCard label="Users" value={stats.users ?? 0} color="#0ea5a4" />
              <StatCard label="Events" value={stats.events ?? 0} color="#6366f1" />
              <StatCard label="Bookings" value={Object.values(stats.bookings || {}).reduce((a, b) => a + b, 0)} color="#f59e42" />
              <StatCard label="Payments" value={Object.values(stats.payments || {}).reduce((a, b) => a + b, 0)} color="#16a34a" />
              <StatCard label="Booths" value={Object.values(stats.booths || {}).reduce((a, b) => a + b, 0)} color="#eab308" />
              <StatCard label="Speakers" value={Object.values(stats.speakers || {}).reduce((a, b) => a + b, 0)} color="#0ea5e9" />
              <StatCard label="Notifications" value={Object.values(stats.notifications || {}).reduce((a, b) => a + b, 0)} color="#f43f5e" />
              <StatCard label="Tickets" value={Object.values(stats.tickets || {}).reduce((a, b) => a + b, 0)} color="#0ea5a4" />
              <StatCard label="Packages" value={Object.values(stats.packages || {}).reduce((a, b) => a + b, 0)} color="#a21caf" />
              <StatCard label="CMS Items" value={stats.cms ?? 0} color="#a21caf" />
              <StatCard label="Total Revenue" value={stats.totalRevenue ?? 0} color="#0ea5a4" prefix="TZS " />
            </Box>

            {/* Status Breakdown Charts */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 4 }}>
              {bookingsData && <Pie data={bookingsData} style={{ maxWidth: 300 }} />}
              {boothsData && <Pie data={boothsData} style={{ maxWidth: 300 }} />}
              {ticketsData && <Pie data={ticketsData} style={{ maxWidth: 300 }} />}
              {paymentsData && <Pie data={paymentsData} style={{ maxWidth: 300 }} />}
            </Box>

            {/* Staff Process Logs Table (for admin/superadmin) */}
            {Array.isArray(stats.staffProcessLogs) && stats.staffProcessLogs.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Staff Process Logs</Typography>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: 8 }}>User ID</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Actions</th>
                      <th style={{ textAlign: 'left', padding: 8 }}>Last Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.staffProcessLogs.map((log, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: 8 }}>{log.userId}</td>
                        <td style={{ padding: 8 }}>{log.actions}</td>
                        <td style={{ padding: 8 }}>{log.lastAction}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </> : <Typography color="text.secondary">No dashboard data available.</Typography>
        }
      </Box>
    </Layout>
  );
}

// Professional stat card component
function StatCard({ label, value, color, prefix }) {
  return (
    <Box sx={{
      background: '#fff',
      borderRadius: 3,
      boxShadow: '0 2px 8px #e6e9ee',
      p: 3,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      minHeight: 100
    }}>
      <Typography sx={{ color: color || '#0ea5a4', fontWeight: 700, fontSize: 18, mb: 1 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 28, color: '#222' }}>
        {prefix}{value ?? 0}
      </Typography>
    </Box>
  );
}

export default AdminDashboard;